import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CreditCard, Landmark, Lock, MapPin, Smartphone, User, Wallet } from "lucide-react";
import { toast } from "sonner";
import { api, inr } from "@/lib/api";
import { useStore } from "@/context/StoreContext";
import { useAuth } from "@/context/AuthContext";
import PaymentModal from "@/components/PaymentModal";
import Reveal from "@/components/Reveal";

const METHODS = [
  { value: "upi", label: "UPI", desc: "Google Pay · PhonePe · Paytm · BHIM", icon: Smartphone },
  { value: "card", label: "Card", desc: "Visa · Mastercard · RuPay · Amex", icon: CreditCard },
  { value: "netbanking", label: "Net Banking", desc: "All major Indian banks", icon: Landmark },
  { value: "cod", label: "Cash on Delivery", desc: "Pay when it arrives (+₹49)", icon: Wallet },
];

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { cart, subtotal, coupon, clearCart } = useStore();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", state: "", pincode: "" });
  const [method, setMethod] = useState("upi");
  const [payOpen, setPayOpen] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [payConfig, setPayConfig] = useState({ live: false });
  const { user } = useAuth();

  useEffect(() => {
    api.getPaymentConfig().then(setPayConfig).catch(() => {});
  }, []);

  useEffect(() => {
    if (user) {
      setForm((f) => ({ ...f, name: f.name || user.name || "", email: f.email || user.email || "" }));
    }
  }, [user]);

  const discount = coupon?.discount || 0;
  const shipping = subtotal - discount >= 999 ? 0 : 99;
  const codFee = method === "cod" ? 49 : 0;
  const total = subtotal - discount + shipping + codFee;

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const validate = () => {
    if (form.name.trim().length < 2) return "Please enter your full name.";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email)) return "Please enter a valid email.";
    if (!/^\d{10,15}$/.test(form.phone.replace(/\s/g, ""))) return "Please enter a valid 10-digit phone number.";
    if (form.address.trim().length < 5) return "Please enter your full address.";
    if (form.city.trim().length < 2 || form.state.trim().length < 2) return "Please enter your city and state.";
    if (!/^\d{6}$/.test(form.pincode)) return "Please enter a valid 6-digit pincode.";
    return null;
  };

  const placeOrder = async (paymentId = null) => {
    setPlacing(true);
    try {
      const order = await api.createOrder({
        items: cart.map((i) => ({ slug: i.slug, variant: i.variant, qty: i.qty })),
        customer: form,
        payment_method: method,
        coupon: coupon?.code || null,
        payment_id: paymentId,
      });
      clearCart();
      navigate(`/order-success/${order.order_id}`, { state: { order } });
    } catch {
      toast.error("Could not place your order. Please try again.");
      setPlacing(false);
      setPayOpen(false);
    }
  };

  const startRazorpay = async () => {
    setPlacing(true);
    try {
      const ok = await loadRazorpayScript();
      if (!ok) throw new Error("script");
      const rzOrder = await api.createRazorpayOrder({
        items: cart.map((i) => ({ slug: i.slug, variant: i.variant, qty: i.qty })),
        coupon: coupon?.code || null,
        payment_method: method,
      });
      const rzp = new window.Razorpay({
        key: rzOrder.key_id,
        amount: rzOrder.amount,
        currency: rzOrder.currency,
        name: "Mother Nature",
        description: "Ancient wisdom. Modern science.",
        order_id: rzOrder.id,
        theme: { color: "#0D1812", backdrop_color: "rgba(7,13,9,0.85)" },
        prefill: { name: form.name, email: form.email, contact: form.phone },
        handler: async (resp) => {
          try {
            const v = await api.verifyPayment(resp);
            await placeOrder(v.payment_id);
          } catch {
            toast.error("Payment verification failed. If money was debited, it will auto-refund.");
            setPlacing(false);
          }
        },
        modal: { ondismiss: () => { setPlacing(false); toast.info("Payment cancelled — your bag is safe."); } },
      });
      rzp.on("payment.failed", () => {
        setPlacing(false);
        toast.error("Payment failed. Please try another method.");
      });
      rzp.open();
    } catch {
      toast.error("Could not open the payment gateway. Please try again.");
      setPlacing(false);
    }
  };

  const submit = () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    if (method === "cod") {
      placeOrder();
    } else if (payConfig.live) {
      startRazorpay();
    } else {
      setPayOpen(true);
    }
  };

  if (cart.length === 0 && !placing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center px-4 text-center" data-testid="checkout-empty">
        <p className="font-serif text-3xl">Your bag is empty.</p>
        <p className="mt-2 text-sm" style={{ color: "var(--mn-text2)" }}>A ritual begins with a single drop.</p>
        <button onClick={() => navigate("/shop")} data-testid="checkout-empty-shop" className="btn-gold mt-8 rounded-full px-8 py-3.5 text-sm">
          Explore the Collection
        </button>
      </div>
    );
  }

  const inputCls = "w-full rounded-xl border bg-transparent px-4 py-3 text-sm outline-none transition-colors focus:border-[var(--mn-gold)] placeholder:text-[var(--mn-text2)]";

  return (
    <div className="mx-auto max-w-6xl px-4 pb-24 pt-28 sm:px-6 lg:px-8" data-testid="checkout-page">
      <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm transition-colors hover:text-[var(--mn-gold)]" style={{ color: "var(--mn-text2)" }} data-testid="checkout-back">
        <ArrowLeft size={15} /> Back
      </button>
      <Reveal>
        <h1 className="mt-4 font-serif text-3xl tracking-tight sm:text-4xl">
          The final step of <span className="gold-text italic">the ritual.</span>
        </h1>
        <p className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: "var(--mn-text2)" }}>
          <Lock size={13} /> Guest checkout — no account needed, fully encrypted.
        </p>
      </Reveal>

      <div className="mt-10 grid grid-cols-1 gap-10 lg:grid-cols-[1fr_400px]">
        <div className="space-y-8">
          <section className="glass-card rounded-3xl p-6 sm:p-8" data-testid="checkout-details">
            <p className="flex items-center gap-2 font-serif text-xl"><User size={18} style={{ color: "var(--mn-gold)" }} /> Delivery Details</p>
            <div className="mt-5 grid grid-cols-1 gap-4 sm:grid-cols-2">
              <input data-testid="checkout-name" value={form.name} onChange={set("name")} placeholder="Full name" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-email" type="email" value={form.email} onChange={set("email")} placeholder="Email address" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-phone" value={form.phone} onChange={set("phone")} placeholder="Phone (10 digits)" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-pincode" value={form.pincode} onChange={set("pincode")} placeholder="Pincode" maxLength={6} className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-address" value={form.address} onChange={set("address")} placeholder="Address (house, street, landmark)" className={`${inputCls} sm:col-span-2`} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-city" value={form.city} onChange={set("city")} placeholder="City" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
              <input data-testid="checkout-state" value={form.state} onChange={set("state")} placeholder="State" className={inputCls} style={{ borderColor: "var(--mn-glass-border)" }} />
            </div>
          </section>

          <section className="glass-card rounded-3xl p-6 sm:p-8" data-testid="checkout-payment">
            <p className="flex items-center gap-2 font-serif text-xl"><MapPin size={18} style={{ color: "var(--mn-mint)" }} /> Payment Method</p>
            <div className="mt-5 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {METHODS.map((m) => (
                <button
                  key={m.value}
                  data-testid={`payment-method-${m.value}`}
                  onClick={() => setMethod(m.value)}
                  className="flex items-start gap-3 rounded-2xl border p-4 text-left transition-all"
                  style={{
                    borderColor: method === m.value ? "var(--mn-gold)" : "var(--mn-glass-border)",
                    background: method === m.value ? "var(--mn-surface)" : "transparent",
                    boxShadow: method === m.value ? "0 0 18px rgba(212,175,55,0.2)" : undefined,
                  }}
                >
                  <m.icon size={19} className="mt-0.5 shrink-0" style={{ color: method === m.value ? "var(--mn-gold)" : "var(--mn-text2)" }} />
                  <span>
                    <span className="block text-sm font-medium">{m.label}</span>
                    <span className="mt-0.5 block text-xs" style={{ color: "var(--mn-text2)" }}>{m.desc}</span>
                  </span>
                </button>
              ))}
            </div>
          </section>
        </div>

        <aside className="glass-card h-fit rounded-3xl p-6 sm:p-8" data-testid="checkout-summary">
          <p className="font-serif text-xl">Order Summary</p>
          <ul className="mt-5 space-y-3">
            {cart.map((i) => (
              <li key={i.key} className="flex items-center gap-3">
                <img src={i.image} alt="" className="h-12 w-10 rounded-lg object-cover" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm">{i.name}</p>
                  <p className="font-mono text-[0.62rem]" style={{ color: "var(--mn-text2)" }}>{i.variant} × {i.qty}</p>
                </div>
                <span className="font-mono text-sm" style={{ color: "var(--mn-gold)" }}>{inr(i.price * i.qty)}</span>
              </li>
            ))}
          </ul>
          <div className="mt-5 space-y-1.5 border-t pt-4 text-sm" style={{ borderColor: "var(--mn-glass-border)" }}>
            <div className="flex justify-between" style={{ color: "var(--mn-text2)" }}>
              <span>Subtotal</span><span data-testid="summary-subtotal">{inr(subtotal)}</span>
            </div>
            {discount > 0 && (
              <div className="flex justify-between" style={{ color: "var(--mn-mint)" }}>
                <span>Coupon {coupon.code}</span><span>−{inr(discount)}</span>
              </div>
            )}
            <div className="flex justify-between" style={{ color: "var(--mn-text2)" }}>
              <span>Shipping</span><span data-testid="summary-shipping">{shipping === 0 ? "Free" : inr(shipping)}</span>
            </div>
            {codFee > 0 && (
              <div className="flex justify-between" style={{ color: "var(--mn-text2)" }}>
                <span>COD fee</span><span>{inr(codFee)}</span>
              </div>
            )}
            <div className="flex justify-between border-t pt-2.5 font-serif text-xl" style={{ borderColor: "var(--mn-glass-border)" }}>
              <span>Total</span>
              <span style={{ color: "var(--mn-gold)" }} data-testid="summary-total">{inr(total)}</span>
            </div>
          </div>
          <motion.button
            data-testid="place-order-button"
            onClick={submit}
            disabled={placing}
            whileTap={{ scale: 0.98 }}
            className="btn-gold mt-6 flex w-full items-center justify-center gap-2 rounded-full py-4 text-sm"
          >
            <Lock size={14} />
            {placing ? "Placing order…" : method === "cod" ? `Place Order · ${inr(total)}` : `Pay ${inr(total)}`}
          </motion.button>
          <p className="mt-3 text-center font-mono text-[0.58rem] uppercase tracking-[0.2em]" style={{ color: "var(--mn-text2)" }} data-testid="payment-mode-note">
            {payConfig.live
              ? "256-bit encrypted · Powered by Razorpay"
              : "Simulated payments · No real charge · Razorpay goes live once keys are added"}
          </p>
        </aside>
      </div>

      <PaymentModal
        open={payOpen}
        method={method}
        amount={total}
        onClose={() => setPayOpen(false)}
        onSuccess={() => placeOrder()}
      />
    </div>
  );
}
